import { useParams } from 'react-router';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { IWarningRatification } from '../../../../../../../models/interfaces/WarningRatification';
import { useProcessing } from '../../../../../../../hooks/useProcessing';
import { useActions } from '../../../../../../../hooks/useActions';
import { updateModalState } from '../../../../../../../store/redux-slices/modals';
import { SUBSCRIPTION_DETAILS_EDITING_PROCESSING } from '../../../../../../../store/redux-slices/processes/constants';
import { organizationNameSelector } from '../../../../../../../store/redux-slices/organizations/selectors';
import { useFormErrorsActions } from '../../../../../../../styledComponents/FormErrorProvider/FormErrorProvider.hooks';

import { useApiResponseFail } from '../../../../../../../hooks/useApiResponseFail';
import { editKESubscriptionOptions } from '../../../../../../../store/saga-slices/subscriptions/kidnapAndExtortion';
import { keSubscriptionOverviewSelector } from '../../../../../../../store/redux-slices/subscriptions/kidnapAndExtortion/selectors';
import { IKEOrganizationSubscriptionOptionsModel } from '../../../../../../../models/interfaces/Subscription/KidnapAndExtortion/KEOrganizationSubscriptionOptionsModel';
import { IEditKESubscriptionOptionsPayload } from '../../../../../../../store/saga-slices/subscriptions/kidnapAndExtortion/models';
import { KidnapAndExtortionSubscriptionOptions } from '../../../../../../../models/enums/KidnapAndExtortionSubscriptionOptions';
import { EDITING_KE_SUBSCRIPTION_OPTIONS_MODAL_NAME } from './keSubscriptionEditOptionsModal.const';

type UseKESubscriptionEditDetailsModalType = {
  onModalSuccess: () => void;
};

export const useKESubscriptionEditDetailsModal = ({
  onModalSuccess,
}: UseKESubscriptionEditDetailsModalType) => {
  const alert = useAlert();
  const { t } = useTranslation();

  const UpdateModalState = useActions(updateModalState);
  const EditSubscriptionDetails = useActions(editKESubscriptionOptions);

  const subscriptionEditing = useProcessing(
    SUBSCRIPTION_DETAILS_EDITING_PROCESSING,
  );

  const subscriptionOverview = useSelector(keSubscriptionOverviewSelector);
  const organizationName = useSelector(organizationNameSelector);

  const { id: organizationId, subscriptionId } = useParams();

  const { validateErrors } = useFormErrorsActions();
  const { handleResponse } = useApiResponseFail();

  const handleSubmit = (values: IKEOrganizationSubscriptionOptionsModel) => {
    editOrganizationSubscription(values);
  };

  const editOrganizationSubscription = (
    values: IKEOrganizationSubscriptionOptionsModel,
    ratifications: IWarningRatification[] = [],
  ) => {
    const payload: IEditKESubscriptionOptionsPayload = {
      organizationId,
      subscriptionId,
      data: values,
      success: () => {
        alert.success(
          t('alerts.subscription-edit-success', {
            value: organizationName,
          }),
        );
        onModalSuccess && onModalSuccess();
        UpdateModalState(EDITING_KE_SUBSCRIPTION_OPTIONS_MODAL_NAME);
      },
      error: (err) => {
        handleResponse({
          err,
          values,
          ratifications,
          alertErrorMessage: t('alerts.subscription-edit-fail', {
            value: organizationName,
          }),
        });
      },
    };
    EditSubscriptionDetails(payload);
  };

  const handleWarningsConfirm = (
    data: IKEOrganizationSubscriptionOptionsModel,
    warnings: IWarningRatification[],
  ) => {
    UpdateModalState([EDITING_KE_SUBSCRIPTION_OPTIONS_MODAL_NAME, { data }]);
    editOrganizationSubscription(data, warnings);
  };

  const onCancelHandler = () =>
    UpdateModalState(EDITING_KE_SUBSCRIPTION_OPTIONS_MODAL_NAME);

  const initData = subscriptionOverview && {
    [KidnapAndExtortionSubscriptionOptions.AutoRenewal]:
      subscriptionOverview.autoRenewal,
  };

  return {
    handleWarningsConfirm,
    handleSubmit,
    validateErrors,
    UpdateModalState,
    subscriptionEditing,
    initData,
    onCancelHandler,
  };
};
